import { FIELD_TYPES } from "./field-types.ts";

/** How a date-family value reads: a calendar day, an instant, or a time of
 *  day. A calendar day and an instant are ONE field type (`date`); the
 *  field's `includeTime` config decides between them, so a field can gain or
 *  lose its time half without changing type and without touching stored
 *  values. A time of day is its own type (`time`): it carries no calendar
 *  day, so day-based filters and relative triggers do not apply to it. */
export type DateKind = "date" | "dateTime" | "time";

/** The structural minimum `dateKindOf` reads. Any field row that selects
 *  `type` and `config.includeTime` is assignable. */
export type DateKindSource = {
  type?: string | null;
  config?: { includeTime?: boolean | null } | null;
};

/** The kind a field's value reads as, or null for a field outside the date
 *  family. The API enum still carries the legacy `dateTime` TYPE (the schema
 *  is additive-only); the server canonicalizes it to `date` + `includeTime`
 *  on write, and a row that still wears it reads as the instant kind. */
export function dateKindOf(
  field: DateKindSource | null | undefined,
): DateKind | null {
  switch (field?.type) {
    case FIELD_TYPES.date:
      return field.config?.includeTime === true ? "dateTime" : "date";
    case FIELD_TYPES.dateTime:
      return "dateTime";
    case FIELD_TYPES.time:
      return "time";
    default:
      return null;
  }
}

export function isChoiceFieldType(type: string | null | undefined): boolean {
  return type === FIELD_TYPES.select || type === FIELD_TYPES.labels;
}

/** True for every type whose value is a date-family instant string. */
export function isDateFieldType(type: string | null | undefined): boolean {
  return (
    type === FIELD_TYPES.date ||
    type === FIELD_TYPES.dateTime ||
    type === FIELD_TYPES.time
  );
}

/** The stored form of a calendar day: `YYYY-MM-DD`, no instant and no zone,
 *  so the day reads the same everywhere and a field that later gains its
 *  time half has nothing to misread. An instant is the ISO string. */
const DAY_STRING_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDayString(value: unknown): value is string {
  return typeof value === "string" && DAY_STRING_RE.test(value);
}

/** The write side of the day boundary: a picker yields LOCAL midnight of
 *  the chosen day; the day it names is its local calendar date. */
export function toDayString(local: Date): string {
  const y = String(local.getFullYear()).padStart(4, "0");
  const m = String(local.getMonth() + 1).padStart(2, "0");
  const d = String(local.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** The read side: a day string as LOCAL midnight of that day, so a calendar
 *  highlights the named day. `new Date("YYYY-MM-DD")` is UTC midnight, which
 *  is the previous local day west of UTC. */
export function dayStringToLocal(day: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}
